import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Lightweight markdown toolbar that dispatches transactions to a CodeMirror
// 6 EditorView. We intentionally avoid the heavy MDEditor toolbar API and
// build a small set of buttons whose behavior is identical across desktop
// and mobile (no popovers, no nested menus).
import { Fragment, useCallback, useEffect, useState } from "react";
// Wrap the current selection with `before` ... `after`. If the selection is
// empty, insert the markers and place the caret between them.
function wrapInline(before, after = before) {
    return (view) => {
        const { state } = view;
        const range = state.selection.main;
        const text = state.sliceDoc(range.from, range.to);
        if (text.length === 0) {
            view.dispatch({
                changes: { from: range.from, insert: before + after },
                selection: { anchor: range.from + before.length },
                userEvent: "input",
            });
        }
        else {
            view.dispatch({
                changes: { from: range.from, to: range.to, insert: before + text + after },
                selection: {
                    anchor: range.from + before.length,
                    head: range.from + before.length + text.length,
                },
                userEvent: "input",
            });
        }
        view.focus();
    };
}
// Add or toggle a per-line prefix (e.g. "# ", "> ", "- "). Runs over every
// line in the current selection so multi-line lists/quotes work in one
// transaction.
function togglePrefix(prefix) {
    return (view) => {
        const { state } = view;
        const range = state.selection.main;
        const startLine = state.doc.lineAt(range.from);
        const endLine = state.doc.lineAt(range.to);
        const changes = [];
        for (let n = startLine.number; n <= endLine.number; n++) {
            const line = state.doc.line(n);
            if (line.text.startsWith(prefix)) {
                changes.push({ from: line.from, to: line.from + prefix.length, insert: "" });
            }
            else {
                changes.push({ from: line.from, insert: prefix });
            }
        }
        view.dispatch({ changes, userEvent: "input" });
        view.focus();
    };
}
// Insert a numbered list — each line of the current selection gets a
// `1. `, `2. ` prefix. If the lines already start with `\d+\. ` we strip it
// instead.
function toggleOrderedList() {
    return (view) => {
        const { state } = view;
        const range = state.selection.main;
        const startLine = state.doc.lineAt(range.from);
        const endLine = state.doc.lineAt(range.to);
        const changes = [];
        let counter = 1;
        let allNumbered = true;
        for (let n = startLine.number; n <= endLine.number; n++) {
            const line = state.doc.line(n);
            if (!/^\d+\.\s/.test(line.text)) {
                allNumbered = false;
                break;
            }
        }
        for (let n = startLine.number; n <= endLine.number; n++) {
            const line = state.doc.line(n);
            if (allNumbered) {
                const match = line.text.match(/^\d+\.\s/);
                if (match) {
                    changes.push({
                        from: line.from,
                        to: line.from + match[0].length,
                        insert: "",
                    });
                }
            }
            else {
                const prefix = `${counter++}. `;
                const existing = line.text.match(/^\d+\.\s/);
                if (existing) {
                    changes.push({
                        from: line.from,
                        to: line.from + existing[0].length,
                        insert: prefix,
                    });
                }
                else {
                    changes.push({ from: line.from, insert: prefix });
                }
            }
        }
        view.dispatch({ changes, userEvent: "input" });
        view.focus();
    };
}
function insertLink() {
    return (view) => {
        const { state } = view;
        const range = state.selection.main;
        const selected = state.sliceDoc(range.from, range.to);
        const text = selected || "link";
        const urlPlaceholder = "https://";
        const insert = `[${text}](${urlPlaceholder})`;
        const urlStart = range.from + text.length + 3; // after `[text](`
        view.dispatch({
            changes: { from: range.from, to: range.to, insert },
            selection: {
                anchor: urlStart,
                head: urlStart + urlPlaceholder.length,
            },
            userEvent: "input",
        });
        view.focus();
    };
}
function insertCodeBlock() {
    return (view) => {
        const { state } = view;
        const range = state.selection.main;
        const selected = state.sliceDoc(range.from, range.to);
        const wrapped = "\n```\n" + (selected || "") + "\n```\n";
        view.dispatch({
            changes: { from: range.from, to: range.to, insert: wrapped },
            selection: selected
                ? { anchor: range.from + 5, head: range.from + 5 + selected.length }
                : { anchor: range.from + 5 },
            userEvent: "input",
        });
        view.focus();
    };
}
// Insert a `<<placeholder>>` template input tag. If text is selected, wrap
// it as `<<selected>>` so the user can promote any string to a placeholder
// in one click. If nothing is selected, insert `<<name>>` and select the
// literal `name` so the user can type a real name immediately.
function insertPlaceholderTag() {
    return (view) => {
        const { state } = view;
        const range = state.selection.main;
        const selected = state.sliceDoc(range.from, range.to);
        if (selected.length > 0) {
            view.dispatch({
                changes: { from: range.from, to: range.to, insert: `<<${selected}>>` },
                selection: {
                    anchor: range.from + 2,
                    head: range.from + 2 + selected.length,
                },
                userEvent: "input",
            });
        }
        else {
            const placeholder = "name";
            view.dispatch({
                changes: { from: range.from, insert: `<<${placeholder}>>` },
                selection: {
                    anchor: range.from + 2,
                    head: range.from + 2 + placeholder.length,
                },
                userEvent: "input",
            });
        }
        view.focus();
    };
}
function selectAll() {
    return (view) => {
        view.dispatch({
            selection: { anchor: 0, head: view.state.doc.length },
            userEvent: "select",
        });
        view.focus();
    };
}
// Paste from the OS clipboard at the current cursor / selection. Wrapped in
// try/catch because navigator.clipboard.readText() may reject for several
// reasons we can't influence: the user denied permission, the page isn't
// in a secure context, the browser blocked the read because the page isn't
// focused, or the API isn't implemented at all (older Safari).
async function pasteFromClipboard(view) {
    let text = "";
    try {
        if (!navigator.clipboard?.readText) {
            throw new Error("Clipboard API unavailable.");
        }
        text = await navigator.clipboard.readText();
    }
    catch {
        alert("Paste failed: clipboard access is unavailable or blocked.");
        view.focus();
        return;
    }
    if (!text) {
        // Empty clipboard isn't an error — silently no-op and keep focus so the
        // user can continue typing.
        view.focus();
        return;
    }
    const range = view.state.selection.main;
    view.dispatch({
        changes: { from: range.from, to: range.to, insert: text },
        selection: { anchor: range.from + text.length },
        userEvent: "input.paste",
        scrollIntoView: true,
    });
    view.focus();
}
// Buttons are grouped by intent (inline marks · headings · blocks · inserts ·
// document) and rendered with a thin divider between groups.
const ACTION_GROUPS = [
    [
        { key: "b", label: "B", title: "Bold (Ctrl/Cmd-B)", run: wrapInline("**") },
        { key: "i", label: "I", title: "Italic (Ctrl/Cmd-I)", run: wrapInline("*") },
        { key: "s", label: "S", title: "Strikethrough", run: wrapInline("~~") },
        { key: "code", label: "</>", title: "Inline code", run: wrapInline("`") },
    ],
    [
        { key: "h1", label: "H1", title: "Heading 1", run: togglePrefix("# ") },
        { key: "h2", label: "H2", title: "Heading 2", run: togglePrefix("## ") },
        { key: "h3", label: "H3", title: "Heading 3", run: togglePrefix("### ") },
    ],
    [
        { key: "ul", label: "•", title: "Bulleted list", run: togglePrefix("- ") },
        { key: "ol", label: "1.", title: "Numbered list", run: toggleOrderedList() },
        { key: "task", label: "☐", title: "Task list", run: togglePrefix("- [ ] ") },
        { key: "quote", label: "❝", title: "Blockquote", run: togglePrefix("> ") },
    ],
    [
        { key: "link", label: "🔗", title: "Insert link", run: insertLink() },
        { key: "codeblock", label: "{ }", title: "Code block", run: insertCodeBlock() },
        { key: "tag", label: "« »", title: "Insert template input tag (<<name>>)", run: insertPlaceholderTag() },
    ],
    [
        { key: "selectall", label: "⌶A", title: "Select all (Ctrl/Cmd-A)", run: selectAll() },
        { key: "paste", label: "⎘", title: "Paste from clipboard", run: (view) => { void pasteFromClipboard(view); } },
    ],
];
export function MarkdownEditorToolbar({ viewRef }) {
    const [ready, setReady] = useState(false);
    // The CodeMirror view isn't available on first render — poll on mount
    // (one rAF is enough since ReactCodeMirror exposes the view synchronously
    // after its first effect).
    useEffect(() => {
        if (viewRef.current?.view) {
            setReady(true);
            return;
        }
        let raf = requestAnimationFrame(function tick() {
            if (viewRef.current?.view) {
                setReady(true);
            }
            else {
                raf = requestAnimationFrame(tick);
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [viewRef]);
    const onClick = useCallback((run) => () => {
        const view = viewRef.current?.view;
        if (!view)
            return;
        run(view);
    }, [viewRef]);
    return (_jsx("div", { role: "toolbar", "aria-label": "Markdown formatting", className: "flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5 dark:border-slate-800 dark:bg-slate-900/60", children: ACTION_GROUPS.map((group, gi) => (_jsxs(Fragment, { children: [gi > 0 && (_jsx("span", { className: "mx-0.5 h-5 w-px bg-slate-200 dark:bg-slate-700", "aria-hidden": true })), group.map(({ key, label, title, run }) => (_jsx("button", { type: "button", title: title, "aria-label": title, disabled: !ready, onClick: onClick(run), 
                    // Keep mousedown from stealing focus from the editor — focus
                    // returns inside the action via view.focus(), but preserving the
                    // selection through the click avoids a visual flicker.
                    onMouseDown: (e) => e.preventDefault(), className: "inline-flex h-9 min-w-9 items-center justify-center rounded-md px-2 text-xs font-medium text-slate-700 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-800", children: label }, key)))] }, gi))) }));
}
